import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormikErrors, FormikHelpers, useFormik } from 'formik'

import { NavigationArrowDownIcon } from '../../../icons/NavigationArrowDownIcon'
import { NameDto } from '../../../model/NameDto'
import { DropdownInput } from '../../../ui/v2/DropdownInput/DropdownInput'
import { Input } from '../../../ui/v2/Input/Input'
import { ModalContentWrapper } from '../../../ui/v2/Modal/ModalContentWrapper'
import { useWindowResize } from '../../../utils/domUtils'
import { isAllowedNumericKey } from '../../../utils/input.utils'
import { isNumber, maxNumber, minNumber } from '../../../utils/validations'

import styles from './RebateSettingsModal.module.scss'

interface RebateSettingsModalProps {
  closeModal: () => void
  onSubmit: (values: Partial<RebateSettingsFormValues>) => Promise<void>
  initialValues?: Partial<RebateSettingsFormValues>
  approvalTypes: NameDto<number>[]
  rebatePeriods: NameDto<number>[]
  onOpenRebateApprovalTypeModal: (values: Partial<RebateSettingsFormValues>) => void
  onOpenRebatePeriodModal: (values: Partial<RebateSettingsFormValues>) => void
}

export const RebateSettingsModal: FC<RebateSettingsModalProps> = (props) => {
  const {
    closeModal,
    onSubmit,
    initialValues,
    approvalTypes,
    rebatePeriods,
    onOpenRebateApprovalTypeModal,
    onOpenRebatePeriodModal,
  } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const { setFieldValue, errors, values, handleSubmit, setErrors, resetForm, isSubmitting } =
    useRebateSettingsForm({
      initialValues: initialValues ?? DEFAULT_VALUES,
      handleSubmit: async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true)
        await onSubmit(values)
        setSubmitting(false)
        resetForm()
      },
    })

  const hasErrors = useMemo(() => Object.values(errors).some((error) => !!error), [errors])

  const isApplyDisabled =
    hasErrors ||
    isSubmitting ||
    !values.rebateApprovalTypeId ||
    !values.rebatePeriodId ||
    !values.rebateAmountInPercentage

  const handleChange = (key: string, value: any) => {
    setFieldValue(key, value)

    if (hasErrors) {
      setErrors({
        ...errors,
        [key]: undefined,
      })
    }
  }

  const handleClose = () => {
    setErrors({})
    resetForm()
    closeModal()
  }

  return (
    <ModalContentWrapper
      title={t('IB.RebatesPage.ClientsPage.Update Rebate settings')}
      applyLabel={t('Apply')}
      isApplyDisabled={isApplyDisabled}
      onClose={handleClose}
      onConfirm={handleSubmit}
      onCancel={handleClose}
    >
      <div className={styles.rebateSettingsModal}>
        {isMobile ? (
          <Input
            value={approvalTypes.find((type) => type.id === values.rebateApprovalTypeId)?.name}
            placeholder={t('IB.RebatesPage.GroupsPage.Group type')}
            readOnly
            onClick={() => onOpenRebateApprovalTypeModal(values)}
            hint={errors.rebateApprovalTypeId}
            error={!!errors.rebateApprovalTypeId}
            endIcon={NavigationArrowDownIcon}
          />
        ) : (
          <DropdownInput
            value={values.rebateApprovalTypeId}
            onChange={(option) => handleChange('rebateApprovalTypeId', option.id)}
            options={approvalTypes}
            placeholder={t('Type')}
            hint={errors.rebateApprovalTypeId}
            error={!!errors.rebateApprovalTypeId}
          />
        )}
        {isMobile ? (
          <Input
            value={rebatePeriods.find((type) => type.id === values.rebatePeriodId)?.name}
            placeholder={t('IB.RebatesPage.GroupsPage.Payout period')}
            readOnly
            onClick={() => onOpenRebatePeriodModal(values)}
            hint={errors.rebatePeriodId}
            error={!!errors.rebatePeriodId}
            endIcon={NavigationArrowDownIcon}
          />
        ) : (
          <DropdownInput
            value={values.rebatePeriodId}
            onChange={(option) => handleChange('rebatePeriodId', option.id)}
            options={rebatePeriods}
            placeholder={t('IB.RebatesPage.Payout period')}
            hint={errors.rebatePeriodId}
            error={!!errors.rebatePeriodId}
          />
        )}
        <Input
          value={values.rebateAmountInPercentage}
          placeholder={t('IB.RebatesPage.Rebate amount in %')}
          onChange={(e) => handleChange('rebateAmountInPercentage', e.target.value)}
          hint={errors.rebateAmountInPercentage}
          error={!!errors.rebateAmountInPercentage}
          onKeyDown={(e) => !isAllowedNumericKey(e) && e.preventDefault()}
        />
      </div>
    </ModalContentWrapper>
  )
}

export interface RebateSettingsFormValues {
  rebateApprovalTypeId: number
  rebatePeriodId: number
  rebateAmountInPercentage: string
}

interface RebateSettingsFormParams {
  handleSubmit: (
    values: Partial<RebateSettingsFormValues>,
    formikHelpers: FormikHelpers<Partial<RebateSettingsFormValues>>
  ) => void
  initialValues?: Partial<RebateSettingsFormValues>
}

const DEFAULT_VALUES: Partial<RebateSettingsFormValues> = {
  rebateApprovalTypeId: undefined,
  rebatePeriodId: undefined,
  rebateAmountInPercentage: undefined,
}

export const useRebateSettingsForm = ({
  handleSubmit,
  initialValues = DEFAULT_VALUES,
}: RebateSettingsFormParams): ReturnType<typeof useFormik<Partial<RebateSettingsFormValues>>> => {
  const { t } = useTranslation()

  return useFormik<Partial<RebateSettingsFormValues>>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validate: (values) => {
      const errors: FormikErrors<RebateSettingsFormValues> = {}

      if (values.rebateApprovalTypeId === undefined) {
        errors.rebateApprovalTypeId = t('Validation.Required')
      }

      if (values.rebatePeriodId === undefined) {
        errors.rebatePeriodId = t('Validation.Required')
      }

      if (!values.rebateAmountInPercentage) {
        errors.rebateAmountInPercentage = t('Validation.Required')
      }

      if (values.rebateAmountInPercentage && !isNumber(values.rebateAmountInPercentage)) {
        errors.rebateAmountInPercentage = t(
          'Validation.Maximum {{amount}} decimals allowed for input',
          {
            amount: 2,
          }
        )
      }

      if (
        values.rebateAmountInPercentage &&
        (minNumber(values.rebateAmountInPercentage, 0.1) ||
          maxNumber(values.rebateAmountInPercentage, 100))
      ) {
        errors.rebateAmountInPercentage = t(
          'IB.RebatesPage.ApprovalsPage.Please enter a valid percentage between 0 and 100'
        )
      }

      return errors
    },
    validateOnChange: false,
  })
}
